/**
 * "Your health information is protected" modal, auto-opened once over the
 * first question. Reuses PortaledModal (portal + dimmer + bg-click close).
 * Trust badges are the existing bundled SVGs (same as FooterV2).
 */

import React from "react";
import LegitScript from "../../../icons/LegitScript.svg";
import PCi from "../../../icons/PCi-logo.svg";
import MadeInGermany from "../../../icons/MadeInGermany.svg";
import DMCA from "../../../icons/DMCA.svg";
import PrimaryCtaButton from "./PrimaryCtaButton";
import VariantBModalShell from "./VariantBModalShell";
import * as translator from "../../../utils/translator";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

interface HealthProtectedModalProps {
  isOpen: boolean;
  onClose: () => void;
  language?: string;
}

const BADGES = [
  { src: LegitScript, alt: "LegitScript" },
  { src: PCi, alt: "PCI" },
  { src: MadeInGermany, alt: "Certified" },
  { src: DMCA, alt: "DMCA" },
];

const CheckIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0 mt-[2px]">
    <path
      d="M4 9.2l3.2 3.2L14 5.6"
      stroke="#269C6E"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HealthProtectedModal: React.FC<HealthProtectedModalProps> = ({ isOpen, onClose, language }) => {
  const points = [
    t(language, "health_modal_point1"),
    t(language, "health_modal_point2"),
    t(language, "health_modal_point3"),
  ];

  return (
    <VariantBModalShell modalId="wl2_health_protected_modal" isOpen={isOpen} onClose={onClose} shouldBackgroundClickHideModal={false}>
      <div className="overflow-y-auto flex-1 min-h-0 pr-1">
        <h2 className="font-['Poppins'] font-semibold text-[26px] leading-[32px] text-[#08080a] pr-8 mb-5">
          {t(language, "health_modal_title")}
        </h2>

        <div className="flex gap-3 mb-5">
          {BADGES.map((b) => (
            <div
              key={b.alt}
              className="flex-1 h-[62px] rounded-[12px] bg-[#f5f7fa] flex items-center justify-center p-2"
            >
              <img src={b.src} alt={b.alt} className="max-h-full max-w-full object-contain" />
            </div>
          ))}
        </div>

        <p className="font-['Poppins'] text-[15px] leading-[22px] text-[#4b5563] mb-4">
          {t(language, "health_modal_desc")}
        </p>

        <div className="rounded-[12px] bg-[#f4faf7] p-4 mb-6 flex flex-col gap-3">
          {points.map((p, i) => (
            <div key={i} className="flex gap-2 items-start">
              <CheckIcon />
              <span className="font-['Poppins'] text-[14px] leading-[20px] text-[#333]">{p}</span>
            </div>
          ))}
        </div>
      </div>

      <PrimaryCtaButton id="wl2_health_protected_continue" label={t(language, "health_modal_continue")} onClick={onClose} />
    </VariantBModalShell>
  );
};

export default HealthProtectedModal;
